import { ConfirmationType, CustomerOidProfileSnapshot, KeyValuePair } from "../backend-types/misc-types";
import { Elector } from "../Services/elector-data.service";

export interface Ballot {
  eventId: number;
  voteEventData: VoteEventData;
  contest_list: HybridContest[];
  voted?: boolean;
  eventType?: string;
  eventMetadata?: EventMetadata;
  eventCreation: string;
  eventActivation: string;
  eventExpiration: string;
  organizationId?: number;
  recurrenceInterval?: string;
  mediaData?: MediaData;
}

export function markedBallot(ballot: Ballot): Ballot {
  const questions: Question[] = [];
    for (let question of ballot.voteEventData.questions) {
      const statements: Statement[] = [];
      for (let statement of question.statements) {
        const selectedChoices = statement.choices.filter(choice => choice.selected)
        if (Array.isArray(selectedChoices) && selectedChoices.length > 0) {
          statements.push( new Statement(
            statement.description,
            selectedChoices,
            statement.maxSelect,
            statement.minSelect,
            statement.statementId,
            )
          )
        }
      }
      if (statements.length > 0) {
        questions.push(new Question(
          question.description ?? "",
          question.questionId,
          statements,
          question.isRCV
        ))
      }
    }
    const marked: Ballot = {
      eventId: ballot.eventId,
      voteEventData: {
        description: ballot.voteEventData.description,
        questions: questions
      },
      eventCreation: ballot.eventCreation,
      eventActivation: ballot.eventActivation,
      eventExpiration: ballot.eventExpiration,
      organizationId: ballot.organizationId,
      contest_list: []
    }
    if(ballot.eventType) {
      marked.eventType = ballot.eventType;
    }
    if(ballot.recurrenceInterval) {
      marked.recurrenceInterval = ballot.recurrenceInterval;
    }
    return marked
}

export class HybridContest {
  statement?: Statement;
  question?: Question;
  selections: Choice[];
  rankedStatements: Statement[];

  get choices(): Choice[] | undefined {
    return this.isRCV ? undefined : this.statement?.choices
  }

  get description(): string | undefined {
    return this.isRCV ? this.question?.description : this.statement?.description
  }

  get description1(): string | undefined {
    return this.isRCV ? this.question?.description1 : this.statement?.description1
  }

  get description2(): string | undefined {
    return this.isRCV ? this.question?.description2 : this.statement?.description2
  }

  get isRCV(): boolean {
    return this.statement === undefined || this.statement === null
  }

  get maxSelect(): number | undefined {
    if(this.isRCV || this.statement?.maxSelect == undefined) {
      return undefined
    } else {
      return +this.statement?.maxSelect
    }
  }

  get minSelect(): number | undefined {
    if(this.isRCV || this.statement?.minSelect == undefined) {
      return undefined
    } else {
      return +this.statement?.minSelect
    }
  }

  get contest_name(): string {
    return this.description + (this.description1 ? ' ' + this.description1 : '');
  }
  
  get unrankedStatements(): Statement[] {
    if (this.isRCV) {
      let question = this.question;
      if (question) {
        return question.statements.filter(statement => !this.rankedStatements.includes(statement))
      } else {
        return []
      }
    } else {
      return []
    }
  }

  constructor(question?: Question, statement?: Statement) {
    this.question = question
    this.statement = statement
    this.selections = []
    this.rankedStatements = []
    if (question) {
      question.rankedStatements = []
    }
    if (statement) {
      statement.selections = []
    }

  }
}

export interface VoteEventData {
  description: string;
  description1?: string;
  description2?: string;
  questions: Question[];
  optionalEventConfiguration?: OptionalEventConfiguration;
  showResults?: boolean;
}

export interface EventMetadata {
  petition: {
    fields: PetitionFields[];
    listOfValues: ListOfValues[];
    petitionText: string;
    template: string;
    textPlaceholders: KeyValuePair[];
    titleField: string;
  }
}

export interface MediaData {
  image?: string,
  imageUrl?: string,
  videoUrl?: string
}

export interface PetitionFields {
  fieldName: string,
  fieldTitle: string,
  fieldType: string,
  isMandatory: boolean,
  fieldTooltip?: string
}

export interface ListOfValues {
  fieldName: string,
  fieldValues: string[]
}

export interface Question { 
  description: string;
  description1?: string;
  description2?: string;
  questionId: string;
  statements: Statement[];
  isRCV?: boolean;
  rankedStatements?: Statement[];
  maxSelect?: string;
}

export class Question implements Question {
  public description: string;
  public description1?: string;
  public description2?: string;
  public questionId: string;
  public statements: Statement[];
  public isRCV?: boolean;
  public rankedStatements?: Statement[]; 
  maxSelect?: string;
  

  constructor(
    description: string,
    questionId: string,
    statements: Statement[],
    isRCV?: boolean,
    rankedStatements?: Statement[],
    maxSelect?: string
    ) {
    this.description = description;
    this.questionId = questionId
    this.statements = statements
    this.isRCV = isRCV
    this.rankedStatements = rankedStatements
    this.maxSelect = maxSelect
  }
}

export interface Statement {
  description: string;
  description1?: string;
  description2?: string;
  choices: Choice[];
  maxSelect: string;
  minSelect: string;
  statementId: string;
  selections: Choice[];
  declineToVoteContest?: boolean;
  isWriteIn?: boolean;
}

export class Statement implements Statement {
  public description: string;
  public description1?: string;
  public description2?: string;
  public choices: Choice[];
  public maxSelect: string;
  public minSelect: string;
  public statementId: string;
  public selections: Choice[];
  public declineToVoteContest?: boolean;
  isWriteIn?: boolean
  

  constructor(
    description: string,
    choices: Choice[],
    maxSelect: string,
    minSelect: string,
    statementId: string,
    description1?: string,
    description2?: string,
    declineToVoteContest?: boolean,
    isWriteIn?: boolean
  ) {
    this.description = description
    this.description1 = description1
    this.description2 = description2
    this.choices = choices
    this.statementId = statementId
    this.maxSelect = maxSelect
    this.minSelect = minSelect
    this.selections = []
    this.declineToVoteContest = declineToVoteContest ?? false
    this.isWriteIn = isWriteIn ?? false
  }
}

export interface Choice {
  description: string;
  choiceId: number;
  selected: boolean;
  nonSelectable?: boolean;
  declineToVoteChoice?: boolean;
  choiceDetails?: ChoiceDetails;
  isWriteIn?: boolean;
  ballotMarkingMetadata?: ballotMarkingMetadata;
}

export interface ballotMarkingData {
  description: string;
  isWriteIn?: boolean;
  ballotMarkingMetadata?: ballotMarkingMetadata;
}

export interface ballotMarkingMetadata {
  height?: string;
  page?: string;
  width?: string;
  x?: string;
  y?: string;
  shape?:string;
  writeInAdjustX?: string;
  writeInAdjustY?: string;
  writeInFontSize?: string;
  writeInMaxLength?: string;
}

export interface ChoiceDetails {
  image: string;
  imageUrl: string;
  webUrl: string;
}

export interface ElectorAndBallot {
  elector?: Elector;
  events?: Ballot[];
  votedEventInfo?: VotedEventRecord[]; 
  error_type?: string;
  precinct?: string;
  ballotType?: string;
  ballotURL?: string;
  envelopeURL?: string;
  orgVerifications?: CustomerOidProfileSnapshot[];
}

export interface OptionalEventConfiguration {
  defaultAffidavitText?: string;
  confirmationDeliveryChannel?: ConfirmationType;
  ovalOnRight?: boolean;
  submitSignature?: boolean;
}

export interface VotedEventRecord {
  eventId: number;
  lastUseTs?: string
}